import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

// Patient fills a practice-built questionnaire (custom_forms). Reached from
// My Forms; org id travels in router state (falls back to searching all
// memberships for the form).

const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] focus:outline-none focus:border-[#0DA7CA] transition-all';

export default function ClientQuestionnairePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { memberships } = useOrg();

  const [form, setForm] = useState(null);
  const [orgId, setOrgId] = useState(location.state?.orgId || null);
  const [orgName, setOrgName] = useState(location.state?.orgName || '');
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const candidates = orgId
        ? [{ org_id: orgId }]
        : (memberships || []);
      for (const m of candidates) {
        try {
          const forms = await api.getMyCustomForms(m.org_id);
          const found = (forms || []).find(f => f.id === id);
          if (found && !cancelled) {
            setForm(found);
            setOrgId(m.org_id);
            if (!orgName) setOrgName(m.organization?.name || '');
            if (found.submitted_at) setDone(true);
            break;
          }
        } catch { /* try next practice */ }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, memberships]);  // orgId/orgName deliberately omitted — set inside this effect

  const setAnswer = (fieldId, value) => setAnswers(a => ({ ...a, [fieldId]: value }));

  const missingRequired = useMemo(() => {
    if (!form) return [];
    return (form.fields || []).filter(f => {
      if (!f.required) return false;
      const v = answers[f.id];
      return v === undefined || v === '' || (Array.isArray(v) && !v.length);
    });
  }, [form, answers]);

  const submit = async () => {
    if (missingRequired.length) { setError(`Please answer: ${missingRequired.map(f => f.label).join(', ')}`); return; }
    setSubmitting(true); setError('');
    try {
      await api.submitCustomForm(orgId, id, answers);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex-1 bg-primary flex items-center justify-center"><div className="h-6 w-40 bg-tertiary rounded animate-pulse" /></div>;
  }

  if (!form) {
    return (
      <div className="flex-1 bg-primary flex items-center justify-center">
        <div className="text-center">
          <p className="text-[13px] text-muted-c mb-4">This form is no longer available.</p>
          <button onClick={() => navigate('/client/forms')} className="text-[13px] font-semibold text-accent-light">Back to My Forms</button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex-1 bg-primary flex items-center justify-center px-4">
        <div className="text-center max-w-[380px]">
          <CheckCircle2 size={44} className="mx-auto mb-4 text-[#10B981]" />
          <h1 className="text-[18px] font-bold text-primary-c mb-2">Thank you!</h1>
          <p className="text-[13px] text-muted-c mb-6">
            Your answers were sent to {orgName || 'your practice'}. They'll review them before your next visit.
          </p>
          <button onClick={() => navigate('/client/forms')} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all">
            Back to My Forms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[620px] mx-auto px-4 md:px-7 py-8">
        <button onClick={() => navigate('/client/forms')} className="text-[12px] text-muted-c hover:text-accent-light transition-colors mb-3">&larr; My Forms</button>
        <h1 className="text-[20px] font-bold text-primary-c mb-1">{form.title}</h1>
        {orgName && <p className="text-[12px] text-muted-c mb-1">{orgName}</p>}
        {form.description && <p className="text-[13px] text-secondary-c mb-6">{form.description}</p>}

        <div className="space-y-5 mt-6">
          {(form.fields || []).map(f => (
            <div key={f.id} className="bg-elevated border border-theme rounded-xl p-4">
              <label className="block text-[13px] font-semibold text-primary-c mb-2.5">
                {f.label}{f.required && <span className="text-danger"> *</span>}
              </label>
              {f.type === 'textarea' && (
                <textarea className={inputClass} rows={3} value={answers[f.id] || ''} onChange={e => setAnswer(f.id, e.target.value)} />
              )}
              {['text', 'email', 'phone', 'number', 'date'].includes(f.type) && (
                <input
                  className={inputClass}
                  type={f.type === 'phone' ? 'tel' : f.type === 'text' ? 'text' : f.type}
                  value={answers[f.id] || ''}
                  onChange={e => setAnswer(f.id, e.target.value)}
                />
              )}
              {f.type === 'select' && (
                <select className={inputClass} value={answers[f.id] || ''} onChange={e => setAnswer(f.id, e.target.value)}>
                  <option value="">Choose…</option>
                  {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              {(f.type === 'radio' || f.type === 'yesno') && (
                <div className="space-y-1.5">
                  {(f.type === 'yesno' ? ['Yes', 'No'] : f.options || []).map(o => (
                    <label key={o} className="flex items-center gap-2.5 text-[13px] text-secondary-c cursor-pointer py-1">
                      <input type="radio" name={f.id} checked={answers[f.id] === o} onChange={() => setAnswer(f.id, o)} />
                      {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === 'checkbox' && (
                <div className="space-y-1.5">
                  {(f.options || []).map(o => {
                    const cur = answers[f.id] || [];
                    const checked = cur.includes(o);
                    return (
                      <label key={o} className="flex items-center gap-2.5 text-[13px] text-secondary-c cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setAnswer(f.id, checked ? cur.filter(x => x !== o) : [...cur, o])}
                        />
                        {o}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {error && <div className="mt-4 px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium">{error}</div>}

        <div className="mt-6 flex justify-end">
          <button
            onClick={submit}
            disabled={submitting}
            className="px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit answers'}
          </button>
        </div>
      </div>
    </div>
  );
}
